import React, { useEffect, useMemo, useState } from 'react';
import { tallyPromptService, TallyPrompt } from '../../services/tallyPromptService';
import { parseUrlParams } from '../../utils/navigation';
import { variableService, VariableRecord } from '../../services/variableService';
import { profileService, ProfileRecord } from '../../services/profileService';

type PromptStatus = 'draft' | 'active' | 'archived';

type EditorState = {
  mode: 'list' | 'edit' | 'create';
  working?: Partial<TallyPrompt>;
};

const defaultNewPrompt = (): Partial<TallyPrompt> => ({
  name: '',
  description: '',
  status: 'draft',
  promptContent: '',
  variables: [],
  tags: []
} as any);

const assemblePreview = (prompt: Partial<TallyPrompt>, values: Record<string, any>) => {
  const base = prompt.promptContent || '';
  const components = Array.isArray(prompt.components) ? prompt.components : [];
  // Simple AND eval
  const matches = (when?: Record<string, any>) => {
    if (!when) return true;
    return Object.entries(when).every(([k, allowed]) => {
      const val = values[k];
      if (Array.isArray(allowed)) return allowed.includes(val);
      if (allowed && typeof allowed === 'object') {
        const inside = Array.isArray((allowed as any).in) ? (allowed as any).in : [];
        const notIn = Array.isArray((allowed as any).notIn) ? (allowed as any).notIn : [];
        if (inside.length && !inside.includes(val)) return false;
        if (notIn.length && notIn.includes(val)) return false;
        return true;
      }
      return val === allowed;
    });
  };
  const orderedComps = [...components].sort((a: any, b: any) => {
    const ap = (a?.priority ?? Number.MAX_SAFE_INTEGER);
    const bp = (b?.priority ?? Number.MAX_SAFE_INTEGER);
    return ap - bp;
  });
  const body = [base, ...orderedComps.filter(c => matches(c.when)).map(c => c.content || ''), (prompt as any).toolGuidance || '']
    .filter(Boolean)
    .join('\n\n');
  // Interpolate
  const interpolated = Object.entries(values).reduce((acc, [k, v]) => {
    try {
      const re = new RegExp(`{{\\s*${k}\\s*}}`, 'gi');
      return acc.replace(re, String(v));
    } catch {
      return acc;
    }
  }, body);
  return interpolated;
};

type ToolDef = {
  name: string;
  description: string;
  trigger: string;
  parametersSchema?: any;
  returns?: string;
};

const TOOL_DEFS: ToolDef[] = [
  {
    name: 'generate_session_metadata',
    description: 'Generate a title and description based on the student’s stated topic.',
    trigger: 'As soon as the student clearly states their topic, and only once per session.',
    parametersSchema: {
      type: 'object',
      properties: {
        title: { type: 'string' },
        description: { type: 'string' },
        topic_category: {
          type: 'string',
          enum: ['presentations', 'meetings', 'negotiations', 'feedback', 'emails', 'networking', 'interviews', 'general']
        }
      },
      required: ['title', 'description', 'topic_category']
    },
    returns: 'Acknowledgement, and UI metadata is stored (title, description, category).'
  },
  {
    name: 'generate_skill_assessment',
    description: 'End-of-session assessment with category scores and feedback.',
    trigger: 'When the student indicates they need to leave or at session wrap-up.',
    parametersSchema: {
      type: 'object',
      properties: {
        motivational_statement: { type: 'string' },
        category_scores: {
          type: 'object',
          required: ['Foundations', 'Share Your Ideas', 'Understand Others', 'Conversation Skills', 'Culture and Context'],
          properties: {
            'Foundations': { type: 'object', properties: { score: { type: 'number' }, assessed: { type: 'boolean' } }, required: ['score','assessed'] },
            'Share Your Ideas': { type: 'object', properties: { score: { type: 'number' }, assessed: { type: 'boolean' } }, required: ['score','assessed'] },
            'Understand Others': { type: 'object', properties: { score: { type: 'number' }, assessed: { type: 'boolean' } }, required: ['score','assessed'] },
            'Conversation Skills': { type: 'object', properties: { score: { type: 'number' }, assessed: { type: 'boolean' } }, required: ['score','assessed'] },
            'Culture and Context': { type: 'object', properties: { score: { type: 'number' }, assessed: { type: 'boolean' } }, required: ['score','assessed'] }
          }
        },
        skills_feedback: {
          type: 'object',
          properties: {
            strengths: { type: 'array', items: { type: 'object', properties: { skill_name: { type: 'string' }, description: { type: 'string' }, example: { type: 'string' } }, required: ['skill_name','description'] }, maxItems: 2 },
            improvement: { type: 'object', properties: { skill_name: { type: 'string' }, description: { type: 'string' }, suggestion: { type: 'string' } }, required: ['skill_name','description','suggestion'] }
          }
        }
      },
      required: ['motivational_statement','category_scores','skills_feedback']
    },
    returns: 'Assessment JSON is returned and rendered into a “Session Skills Report” workspace card.'
  },
  {
    name: 'create_concept_card',
    description: 'Create a render-ready concept overview (markdown).',
    trigger: 'When the student asks for an explanation/overview, at milestones, or at session end.',
    parametersSchema: {
      type: 'object',
      properties: {
        transcript: { type: 'array', items: { type: 'object', required: ['role','text'], properties: { role: { type: 'string' }, text: { type: 'string' } } } },
        topic: { type: 'string' },
        description: { type: 'string' },
        specifics: { type: 'array', items: { type: 'string' } },
        duration: { type: 'integer' }
      }
    },
    returns: 'Markdown content that is added as a Concept/Feedback card in the workspace.'
  },
  {
    name: 'create_exercise_card',
    description: 'Generate practice exercises (currently MCQ) from context.',
    trigger: 'When the student requests practice/testing, or repeated mistakes are detected.',
    parametersSchema: {
      type: 'object',
      properties: {
        conversation_text: { type: 'string' },
        mcq_prompt: { type: 'string' },
        abilities: { type: 'array', items: { type: 'string' } },
        skills: { type: 'array', items: { type: 'string' } }
      },
      required: ['conversation_text']
    },
    returns: 'Exercise questions JSON; the UI renders them as interactive exercise cards.'
  },
  {
    name: 'create_vocab_card',
    description: 'Create a vocabulary card with word, definition, and examples.',
    trigger: 'When the student asks “what does [word] mean?” or indicates confusion.',
    parametersSchema: {
      type: 'object',
      properties: {
        word: { type: 'string' },
        definition: { type: 'string' },
        examples: { type: 'array', items: { type: 'string' } },
        topic: { type: 'string' },
        context: { type: 'string' }
      },
      required: ['word','definition']
    },
    returns: 'Card content JSON; the UI adds the vocabulary card to the workspace.'
  }
];

const PromptStudio: React.FC<{ currentUserEmail?: string | null }> = ({ currentUserEmail }) => {
  const [activeTab, setActiveTab] = useState<'prompts' | 'variables' | 'profiles' | 'tools'>('prompts');
  const [statusFilter, setStatusFilter] = useState<PromptStatus | 'all'>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prompts, setPrompts] = useState<TallyPrompt[]>([]);
  const [editor, setEditor] = useState<EditorState>({ mode: 'list' });
  const [selectedPrompt, setSelectedPrompt] = useState<TallyPrompt | null>(null);
  // Variables state
  const [variables, setVariables] = useState<VariableRecord[]>([]);
  const [varEditor, setVarEditor] = useState<null | (Partial<VariableRecord> & { isNew?: boolean })>(null);
  // Run setup state for prompts
  const [activeVariablesForRun, setActiveVariablesForRun] = useState<VariableRecord[]>([]);
  const [runValues, setRunValues] = useState<Record<string, string>>({});
  const [runSetupOpen, setRunSetupOpen] = useState(false);
  const [selectedVariable, setSelectedVariable] = useState<VariableRecord | null>(null);
  const [subValues, setSubValues] = useState<Record<string, string>>({});
  const [previewIssues, setPreviewIssues] = useState<string | null>(null);
  // Tools state (read-only)
  const [selectedToolIdx, setSelectedToolIdx] = useState<number>(0);
  // Profiles (for runtime variable presets)
  const [profiles, setProfiles] = useState<ProfileRecord[]>([]);
  const [selectedProfileName, setSelectedProfileName] = useState<string>('');
  const [selectedProfile, setSelectedProfile] = useState<ProfileRecord | null>(null);
  const [profileEditor, setProfileEditor] = useState<null | (Partial<ProfileRecord> & { isNew?: boolean })>(null);
  // Runtime Realtime config
  const [runtimeModel, setRuntimeModel] = useState<'gpt-realtime' | 'gpt-realtime-mini'>('gpt-realtime');
  const [runtimeVoice, setRuntimeVoice] = useState<string>('marin');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'prompts') {
        const data = await tallyPromptService.listPrompts(statusFilter === 'all' ? undefined as any : (statusFilter as PromptStatus));
        setPrompts((data.prompts || []).sort((a: any, b: any) => (new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())));
        // Load active profiles for quick apply in preview
        try {
          const p = await profileService.list('active');
          setProfiles((p.profiles || []).sort((a, b) => (new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())));
        } catch (e) {
          // Don't block prompts load if profiles fail
          setProfiles([]);
        }
      } else if (activeTab === 'variables') {
        const data = await variableService.list('active');
        setVariables((data.variables || []).sort((a, b) => (new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())));
      } else if (activeTab === 'profiles') {
        // Load profiles and active variables (for allowed values in editor)
        const [p, v] = await Promise.allSettled([
          profileService.list('active'),
          variableService.list('active')
        ]);
        if (p.status === 'fulfilled') {
          setProfiles((p.value.profiles || []).sort((a, b) => (new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())));
        } else {
          setProfiles([]);
        }
        if (v.status === 'fulfilled') {
          setVariables((v.value.variables || []).sort((a, b) => (new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())));
        }
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [statusFilter, activeTab]);

  // Auto-select first prompt / variable on load in list mode
  useEffect(() => {
    if (activeTab === 'prompts' && editor.mode === 'list' && !selectedPrompt && prompts.length > 0) {
      selectPrompt(prompts[0]);
    }
  }, [activeTab, editor.mode, prompts, selectedPrompt]);
  useEffect(() => {
    if (activeTab === 'variables' && !varEditor && !selectedVariable && variables.length > 0) {
      setSelectedVariable(variables[0]);
    }
  }, [activeTab, variables, selectedVariable, varEditor]);
  useEffect(() => {
    if (activeTab === 'profiles' && !profileEditor && !selectedProfile && profiles.length > 0) {
      setSelectedProfile(profiles[0]);
    }
  }, [activeTab, profiles, selectedProfile, profileEditor]);

  const beginCreate = () => {
    setEditor({ mode: 'create', working: defaultNewPrompt() });
    setActiveVariablesForRun([]);
    setRunValues({});
  };

  const beginEdit = (p: TallyPrompt) => {
    setEditor({ mode: 'edit', working: { ...p } });
    setSelectedPrompt(p);
    // Load active variables for run setup
    (async () => {
      try {
        const data = await variableService.list('active');
        const vars = data.variables || [];
        setActiveVariablesForRun(vars);
        const seed: Record<string, string> = {};
        vars.forEach(v => {
          const def = (v.defaultValue as any) || '';
          seed[v.variableName] = def || (v.allowedValues?.[0] || '');
        });
        setRunValues(seed);
      } catch {
        setActiveVariablesForRun([]);
        setRunValues({});
      }
    })();
  };

  const cancelEdit = () => {
    setEditor({ mode: 'list' });
    setSelectedPrompt(null);
    setActiveVariablesForRun([]);
    setRunValues({});
  };

  // Profiles CRUD
  const beginCreateProfile = () => {
    setProfileEditor({
      isNew: true,
      profileName: '',
      description: '',
      values: {},
      status: 'active'
    } as any);
  };
  const beginEditProfile = (p: ProfileRecord) => {
    setProfileEditor({ ...p, isNew: false });
    setSelectedProfile(p);
  };
  const cancelProfileEdit = () => {
    setProfileEditor(null);
  };
  const saveProfile = async () => {
    if (!profileEditor) return;
    const w = profileEditor;
    if (w.isNew && !w.profileName) {
      setError('Profile name is required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload: any = {
        description: w.description || '',
        values: w.values || {},
        status: (w.status as any) || 'active',
        createdBy: currentUserEmail || 'system'
      };
      if (w.isNew) {
        payload.profileName = w.profileName;
        const created = await profileService.create(payload);
        setProfileEditor({ ...created, isNew: false });
        await load();
      } else if (w.profileName) {
        const updated = await profileService.update(w.profileName, payload);
        setProfileEditor({ ...updated, isNew: false });
        await load();
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };
  const archiveProfile = async (p: ProfileRecord) => {
    if (!window.confirm(`Archive profile "${p.profileName}"?`)) return;
    setLoading(true);
    try {
      await profileService.archive(p.profileName);
      await load();
      if (selectedProfile?.profileName === p.profileName) {
        setSelectedProfile(null);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to archive profile');
    } finally {
      setLoading(false);
    }
  };
  const selectProfile = (p: ProfileRecord) => {
    setSelectedProfile(p);
  };

  const savePrompt = async () => {
    if (!editor.working) return;
    const w = editor.working;
    if (!w.name || !w.status) {
      setError('Name and status are required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (editor.mode === 'create') {
        const created = await tallyPromptService.createPrompt({
          ...w,
          createdBy: currentUserEmail || 'system'
        } as any);
        setEditor({ mode: 'edit', working: created });
        await load();
      } else if (editor.mode === 'edit' && (w as any).promptId) {
        const updated = await tallyPromptService.updatePrompt((w as any).promptId, w as any);
        setEditor({ mode: 'edit', working: updated });
        await load();
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to save prompt');
    } finally {
      setLoading(false);
    }
  };

  const archivePrompt = async (p: TallyPrompt) => {
    if (!window.confirm(`Archive prompt "${p.name}"?`)) return;
    setLoading(true);
    try {
      await tallyPromptService.deletePrompt(p.promptId);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to archive prompt');
    } finally {
      setLoading(false);
    }
  };

  const selectPrompt = (p: TallyPrompt) => {
    setSelectedPrompt(p);
    (async () => {
      try {
        const data = await variableService.list('active');
        const vars = data.variables || [];
        setActiveVariablesForRun(vars);
        const seed: Record<string, string> = {};
        vars.forEach(v => {
          const def = (v.defaultValue as any) || '';
          seed[v.variableName] = def || (v.allowedValues?.[0] || '');
        });
        setRunValues(seed);
      } catch {
        setActiveVariablesForRun([]);
        setRunValues({});
      }
    })();
  };

  // Compute substituted values for preview (handles component-mode variables)
  useEffect(() => {
    // Determine which variables are actually referenced in the current prompt text (via {{var}})
    const targetPrompt: any = editor.mode === 'list' ? (selectedPrompt as any) : (editor.working as any);
    const base = targetPrompt?.promptContent || '';
    const comps = Array.isArray(targetPrompt?.components) ? targetPrompt.components : [];
    const toolG = targetPrompt?.toolGuidance || '';
    const combinedText = [base, ...comps.map((c: any) => c?.content || ''), toolG].filter(Boolean).join('\n');
    const referencedVars = new Set<string>();
    try {
      const re = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
      let m: RegExpExecArray | null;
      while ((m = re.exec(combinedText)) !== null) {
        if (m[1]) referencedVars.add(m[1]);
      }
    } catch {}

    if (!activeVariablesForRun || activeVariablesForRun.length === 0) {
      setSubValues(runValues);
      setPreviewIssues(null);
      return;
    }
    const issues: string[] = [];
    const out: Record<string, string> = {};
    Object.entries(runValues).forEach(([k, v]) => {
      const rec = activeVariablesForRun.find(r => r.variableName === k);
      if (rec && rec.substitutionMode === 'component') {
        const mapped = (rec.componentMap as any)?.[v]?.text;
        if (mapped && String(mapped).trim().length > 0) {
          out[k] = String(mapped);
        } else {
          out[k] = String(v ?? '');
          // Only report if the variable is actually referenced as a placeholder AND has no value
          if (referencedVars.has(k) && (!v || String(v).trim() === '')) {
            issues.push(`Missing value for ${k}`);
          }
        }
      } else {
        out[k] = String(v ?? '');
        if (referencedVars.has(k) && (!v || String(v).trim() === '')) {
          issues.push(`Missing value for ${k}`);
        }
      }
    });
    setSubValues(out);
    setPreviewIssues(issues.length ? issues.join(' • ') : null);
  }, [runValues, activeVariablesForRun, editor.mode, selectedPrompt, editor.working]);

  const previewText = useMemo(() => {
    const target = editor.mode === 'list' ? (selectedPrompt as any) : (editor.working as any);
    return assemblePreview(target || {}, subValues);
  }, [editor.mode, selectedPrompt, editor.working, subValues]);

  const launchWithThisPrompt = () => {
    const p = (editor.mode === 'list' ? (selectedPrompt as any) : (editor.working as any));
    if (!p?.promptId) {
      setError('Please save the prompt first to obtain a promptId.');
      return;
    }
    if (previewIssues) {
      setError(`Cannot launch due to preview issues: ${previewIssues}`);
      return;
    }
    // Ensure all selected variable values are non-empty
    const emptyVars = Object.entries(runValues || {}).filter(([_, v]) => !v || String(v).trim() === '');
    if (emptyVars.length > 0) {
      setError(`Please select values for: ${emptyVars.map(([k]) => k).join(', ')}`);
      return;
    }
    const urlParams = new URLSearchParams({
      project: 'talk-to-tally-v2',
      autoStart: 'true',
      promptId: p.promptId,
      vars: JSON.stringify(runValues || {}),
      debug: 'true',
      rtModel: runtimeModel,
      voice: runtimeVoice
    });
    window.open(`${window.location.pathname}?${urlParams.toString()}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="h-screen flex flex-col bg-background">
      <div className="border-b border-border bg-gray-50">
        <div className="px-6 pt-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold text-purple-900 mb-4">Prompt Studio</h1>
            <div className="flex items-center gap-3 mb-4">
              {activeTab === 'prompts' ? (
                <>
              <select
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as PromptStatus)}
                  >
                <option value="all">All</option>
                    <option value="active">Active</option>
                    <option value="draft">Draft</option>
                    <option value="archived">Archived</option>
                  </select>
                  <button
                    onClick={beginCreate}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    New Prompt
                  </button>
                </>
              ) : activeTab === 'variables' ? (
                <>
                  <button
                    onClick={() => setVarEditor({
                      isNew: true,
                      variableName: '',
                      description: '',
                      type: 'string',
                      substitutionMode: 'value',
                      allowedValues: [],
                      defaultValue: undefined,
                      status: 'active',
                    })}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    New Variable
                  </button>
                </>
              ) : activeTab === 'profiles' ? (
                <>
                  <button
                    onClick={beginCreateProfile}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    New Profile
                  </button>
                </>
              ) : null}
            </div>
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('prompts')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === 'prompts'
                  ? 'bg-white text-purple-900 border-x border-t border-gray-200'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              Prompts
            </button>
            <button
              onClick={() => setActiveTab('variables')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === 'variables'
                  ? 'bg-white text-purple-900 border-x border-t border-gray-200'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              Variables
            </button>
            <button
              onClick={() => setActiveTab('profiles')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === 'profiles'
                  ? 'bg-white text-purple-900 border-x border-t border-gray-200'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              Profiles
            </button>
            <button
              onClick={() => setActiveTab('tools')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === 'tools'
                  ? 'bg-white text-purple-900 border-x border-t border-gray-200'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              Tools
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mx-6 mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>{error}</p>
        </div>
      )}

      {activeTab === 'prompts' && editor.mode === 'list' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-6">
          <div className="lg:col-span-6">
            {loading && <div className="text-sm text-gray-500 mb-2">Loading...</div>}
            <div className="space-y-2">
              {prompts.map((p) => {
                const isSelected = selectedPrompt?.promptId === p.promptId;
                return (
                  <div
                    key={p.promptId}
                    className={`border rounded-lg p-4 hover:shadow-sm bg-white cursor-pointer ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
                    onClick={() => selectPrompt(p)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">{p.name}</div>
                        <div className="text-xs text-gray-500">{p.description || 'No description'}</div>
                        <div className="text-xs text-gray-400">
                          {`Created by ${p.createdBy || 'system'}`}{p.createdAt ? ` • ${new Date(p.createdAt).toLocaleString()}` : ''}
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded ${p.status === 'active' ? 'bg-green-100 text-green-800' : p.status === 'draft' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'}`}>
                        {p.status}
                      </span>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); beginEdit(p); }}
                        className="px-2 py-1 text-sm border rounded hover:bg-gray-50"
                      >
                        Edit
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); archivePrompt(p); }}
                        className="px-2 py-1 text-sm border rounded hover:bg-gray-50"
                      >
                        Archive
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); selectPrompt(p); setTimeout(() => launchWithThisPrompt(), 0); }}
                        className="px-2 py-1 text-sm border rounded hover:bg-gray-50"
                      >
                        Launch
                      </button>
                    </div>
                  </div>
                );
              })}
              {prompts.length === 0 && !loading && (
                <div className="text-sm text-gray-500">No prompts yet. Click “New Prompt” to create one.</div>
              )}
            </div>
          </div>
          <div className="lg:col-span-6">
            {selectedPrompt ? (
              <div className="bg-white border rounded p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-800">Preview &amp; Test</h3>
                  <button
                    onClick={launchWithThisPrompt}
                    className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                  >
                    Launch with this Prompt
                  </button>
                </div>
                {previewIssues && (
                  <div className="mb-3 rounded border border-yellow-300 bg-yellow-50 text-yellow-800 px-3 py-2 text-sm">
                    {previewIssues}
                  </div>
                )}
                <div className="mb-3">
                  <div className="text-sm text-gray-700 mb-1">Variable values</div>
                  {/* Profile quick apply (list mode) */}
                  <div className="mb-2 rounded-md border border-indigo-200 bg-indigo-50 p-2">
                    <div className="grid grid-cols-2 gap-2 items-center">
                      <label className="text-sm font-medium text-indigo-900">Profile preset</label>
                      <select
                        className="border rounded px-2 py-1 text-sm bg-white border-indigo-300"
                        value={selectedProfileName}
                        onChange={(e) => {
                          const name = e.target.value;
                          setSelectedProfileName(name);
                          const prof = profiles.find(p => p.profileName === name);
                          const mapped: Record<string, string> = {};
                          activeVariablesForRun.forEach(v => {
                            mapped[v.variableName] = prof?.values?.[v.variableName] ?? '';
                          });
                          setRunValues(mapped);
                        }}
                      >
                        <option value="">(none)</option>
                        {profiles.map(p => (
                          <option key={p.profileName} value={p.profileName}>{p.profileName}</option>
                        ))}
                      </select>
                    </div>
                    <div className="text-[11px] text-indigo-700 mt-1">Selecting a profile sets initial values. You can still adjust variables below.</div>
                  </div>
                  {/* Runtime config (list mode) */}
                  <div className="grid grid-cols-2 gap-2 items-center mb-2">
                    <label className="text-sm text-gray-600">Realtime model</label>
                    <select
                      className="border rounded px-2 py-1 text-sm"
                      value={runtimeModel}
                      onChange={(e) => setRuntimeModel(e.target.value as any)}
                    >
                      <option value="gpt-realtime">gpt-realtime</option>
                      <option value="gpt-realtime-mini">gpt-realtime-mini</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2 items-center mb-4">
                    <label className="text-sm text-gray-600">Voice</label>
                    <select
                      className="border rounded px-2 py-1 text-sm"
                      value={runtimeVoice}
                      onChange={(e) => setRuntimeVoice(e.target.value)}
                    >
                      <option value="marin">marin</option>
                      <option value="coral">coral</option>
                      <option value="verse">verse</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    {activeVariablesForRun.map((v) => (
                      <div key={v.variableName} className="grid grid-cols-2 gap-2 items-center">
                        <label className="text-sm text-gray-600">{v.variableName}</label>
                        <select
                          className="border rounded px-2 py-1 text-sm"
                          value={runValues[v.variableName] || ''}
                          onChange={(e) => setRunValues(prev => ({ ...prev, [v.variableName]: e.target.value }))}
                        >
                          <option value="">{'(blank)'}</option>
                          {(v.allowedValues || []).map(val => (<option key={val} value={val}>{val}</option>))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-700 mb-1">Assembled Prompt</div>
                  <pre className="whitespace-pre-wrap text-sm border rounded p-3 bg-gray-50 max-h-[500px] overflow-auto">
                    {previewText || 'Nothing to preview yet.'}
                  </pre>
                </div>
              </div>
            ) : (
              <div className="bg-white border rounded p-4 text-sm text-gray-500">
                Select a prompt on the left to preview it, or click “New Prompt” to create one.
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'prompts' && editor.mode !== 'list' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-6">
          <div className="lg:col-span-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-800">{editor.mode === 'create' ? 'Create Prompt' : 'Edit Prompt'}</h3>
              <div className="flex gap-2">
                <button onClick={cancelEdit} className="px-3 py-1 border rounded text-sm hover:bg-gray-50">Back</button>
                <button onClick={savePrompt} className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700" disabled={loading}>
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Name</label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={editor.working?.name || ''}
                  onChange={e => setEditor(s => ({ ...s, working: { ...(s.working || {}), name: e.target.value } }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Description</label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={editor.working?.description || ''}
                  onChange={e => setEditor(s => ({ ...s, working: { ...(s.working || {}), description: e.target.value } }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Status</label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={(editor.working?.status as any) || 'draft'}
                  onChange={e => setEditor(s => ({ ...s, working: { ...(s.working || {}), status: e.target.value as any } }))}
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Prompt Content</label>
                <textarea
                  className="w-full border rounded px-3 py-2 min-h-[180px]"
                  value={editor.working?.promptContent || ''}
                  onChange={e => setEditor(s => ({ ...s, working: { ...(s.working || {}), promptContent: e.target.value } }))}
                  placeholder="Write the base prompt. Use {{variables}} to reference values."
                />
              </div>
              {/* Components editor (simple) */}
              <div>
                <div className="flex items-center justify-between">
                  <label className="block text-sm text-gray-700">Components</label>
                  <button
                    onClick={() => {
                      const comps = [...((editor.working as any).components || [])];
                      comps.push({ name: '', description: '', priority: (comps.length + 1), when: {}, content: '' });
                      setEditor(s => ({ ...s, working: { ...(s.working || {}), components: comps } as any }));
                    }}
                    className="text-sm px-2 py-1 border rounded hover:bg-gray-50"
                  >
                    Add Component
                  </button>
                </div>
                <div className="mt-2 space-y-3">
                  {(((editor.working as any)?.components) || []).map((c: any, idx: number) => (
                    <div key={idx} className="border rounded p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium text-gray-800">{c.name || `Component ${idx + 1}`}</div>
                        <button
                          className="px-2 py-1 text-xs border border-red-300 text-red-700 rounded hover:bg-red-50"
                          aria-label="Remove component"
                          onClick={() => {
                            if (!window.confirm(`Remove component "${c.name || 'unnamed'}"?`)) return;
                            const existing = [...((editor.working as any).components || [])];
                            existing.splice(idx, 1);
                            const normalized = existing.map((comp: any, i: number) =>
                              comp && Object.prototype.hasOwnProperty.call(comp, 'priority')
                                ? { ...comp, priority: i + 1 }
                                : comp
                            );
                            setEditor(s => ({ ...s, working: { ...(s.working || {}), components: normalized } as any }));
                          }}
                        >
                          Remove
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          className="border rounded px-2 py-1 text-sm"
                          placeholder="name"
                          value={c.name || ''}
                          onChange={e => {
                            const comps = [...((editor.working as any).components || [])];
                            comps[idx] = { ...c, name: e.target.value };
                            setEditor(s => ({ ...s, working: { ...(s.working || {}), components: comps } as any }));
                          }}
                        />
                        <input
                          className="border rounded px-2 py-1 text-sm"
                          placeholder="priority (number)"
                          value={String(c.priority ?? '')}
                          onChange={e => {
                            const n = parseInt(e.target.value || '0', 10) || 0;
                            const comps = [...((editor.working as any).components || [])];
                            comps[idx] = { ...c, priority: n };
                            setEditor(s => ({ ...s, working: { ...(s.working || {}), components: comps } as any }));
                          }}
                        />
                      </div>
                      <div className="mt-2">
                        <input
                          className="border rounded px-2 py-1 text-sm w-full"
                          placeholder="description"
                          value={c.description || ''}
                          onChange={e => {
                            const comps = [...((editor.working as any).components || [])];
                            comps[idx] = { ...c, description: e.target.value };
                            setEditor(s => ({ ...s, working: { ...(s.working || {}), components: comps } as any }));
                          }}
                        />
                      </div>
                      <div className="mt-2">
                        <label className="block text-sm text-gray-700 mb-1">When (conditions)</label>
                        <div className="space-y-2">
                          {(Object.entries(c.when || {}) as any).map(([varName, allowed]: [string, any]) => (
                            <div key={varName} className="flex items-start gap-2">
                              <div className="min-w-[160px]">
                                <div className="flex items-center gap-2">
                                  <select
                                    className="border rounded px-2 py-1 text-sm w-full"
                                    value={varName}
                                    onChange={e => {
                                      const newVarName = e.target.value;
                                      const comps = [...((editor.working as any).components || [])];
                                      const when = { ...(c.when || {}) };
                                      const vals = when[varName];
                                      delete when[varName];
                                      when[newVarName] = vals;
                                      comps[idx] = { ...c, when };
                                      setEditor(s => ({ ...s, working: { ...(s.working || {}), components: comps } as any }));
                                    }}
                                  >
                                    <option value="">Select variable</option>
                                    {activeVariablesForRun.map(v => (
                                      <option key={v.variableName} value={v.variableName}>{v.variableName}</option>
                                    ))}
                                  </select>
                                  <button
                                    className="h-6 w-6 flex-shrink-0 flex items-center justify-center rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100"
                                    aria-label={`Remove condition ${varName}`}
                                    title="Remove condition"
                                    onClick={() => {
                                      if (!window.confirm(`Remove condition "${varName}"?`)) return;
                                      const comps = [...((editor.working as any).components || [])];
                                      const when = { ...(c.when || {}) };
                                      delete when[varName];
                                      comps[idx] = { ...c, when };
                                      setEditor(s => ({ ...s, working: { ...(s.working || {}), components: comps } as any }));
                                    }}
                                  >
                                    ×
                                  </button>
                                </div>
                              </div>
                              <div className="flex-1">
                                <select
                                  multiple
                                  className="border rounded px-2 py-1 text-sm w-full"
                                  value={Array.isArray(allowed) ? allowed : []}
                                  onChange={e => {
                                    const selected = Array.from(e.target.selectedOptions).map(o => o.value);
                                    const comps = [...((editor.working as any).components || [])];
                                    const when = { ...(c.when || {}) };
                                    when[varName] = selected;
                                    comps[idx] = { ...c, when };
                                    setEditor(s => ({ ...s, working: { ...(s.working || {}), components: comps } as any }));
                                  }}
                                >
                                  {(activeVariablesForRun.find(v => v.variableName === varName)?.allowedValues || []).map(v => (
                                    <option key={v} value={v}>{v}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          ))}
                          {/* Add condition row */}
                          <div className="grid grid-cols-2 gap-2">
                            <select
                              className="border rounded px-2 py-1 text-sm"
                              value=""
                              onChange={e => {
                                const newVarName = e.target.value;
                                if (!newVarName) return;
                                const comps = [...((editor.working as any).components || [])];
                                const when = { ...(c.when || {}) };
                                if (!when[newVarName]) when[newVarName] = [];
                                comps[idx] = { ...c, when };
                                setEditor(s => ({ ...s, working: { ...(s.working || {}), components: comps } as any }));
                              }}
                            >
                              <option value="">Add variable condition…</option>
                              {activeVariablesForRun
                                .filter(v => !(c.when || {})[v.variableName])
                                .map(v => (<option key={v.variableName} value={v.variableName}>{v.variableName}</option>))}
                            </select>
                            <div />
                          </div>
                        </div>
                      </div>
                      <div className="mt-2">
                        <label className="block text-sm text-gray-700 mb-1">Component Content</label>
                        <textarea
                          className="w-full border rounded px-3 py-2 min-h-[120px]"
                          value={c.content || ''}
                          onChange={e => {
                            const comps = [...((editor.working as any).components || [])];
                            comps[idx] = { ...c, content: e.target.value };
                            setEditor(s => ({ ...s, working: { ...(s.working || {}), components: comps } as any }));
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Tool guidance editor */}
              <div>
                <label className="block text-sm text-gray-700 mb-1">Tool Guidance (optional)</label>
                <textarea
                  className="w-full border rounded px-3 py-2 min-h-[120px]"
                  value={(editor.working as any)?.toolGuidance || ''}
                  onChange={e => setEditor(s => ({ ...s, working: { ...(s.working || {}), toolGuidance: e.target.value } }))}
                  placeholder="Write explicit guidance for when and how to call tools (e.g., vocab, concept, exercises). This is appended to the assembled prompt for Prompt Studio launches."
                />
                <div className="text-xs text-gray-500 mt-1">
                  Appended only for Prompt Studio launches. Default Tally tool guidance is not included.
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-800">Preview & Test</h3>
              <button
                onClick={launchWithThisPrompt}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
              >
                Launch with this Prompt
              </button>
            </div>
            <div className="mb-3">
              <div className="text-sm text-gray-700 mb-1">Variable values</div>
              {/* Profile quick apply */}
              <div className="mb-2 rounded-md border border-indigo-200 bg-indigo-50 p-2">
                <div className="grid grid-cols-2 gap-2 items-center">
                  <label className="text-sm font-medium text-indigo-900">Profile preset</label>
                  <select
                    className="border rounded px-2 py-1 text-sm bg-white border-indigo-300"
                    value={selectedProfileName}
                    onChange={(e) => {
                      const name = e.target.value;
                      setSelectedProfileName(name);
                      const prof = profiles.find(p => p.profileName === name);
                      const mapped: Record<string, string> = {};
                      activeVariablesForRun.forEach(v => {
                        mapped[v.variableName] = prof?.values?.[v.variableName] ?? '';
                      });
                      setRunValues(mapped);
                    }}
                  >
                    <option value="">(none)</option>
                    {profiles.map(p => (
                      <option key={p.profileName} value={p.profileName}>{p.profileName}</option>
                    ))}
                  </select>
                </div>
                <div className="text-[11px] text-indigo-700 mt-1">Selecting a profile sets initial values. You can still adjust variables below.</div>
              </div>
              {/* Runtime config */}
              <div className="grid grid-cols-2 gap-2 items-center mb-2">
                <label className="text-sm text-gray-600">Realtime model</label>
                <select
                  className="border rounded px-2 py-1 text-sm"
                  value={runtimeModel}
                  onChange={(e) => setRuntimeModel(e.target.value as any)}
                >
                  <option value="gpt-realtime">gpt-realtime</option>
                  <option value="gpt-realtime-mini">gpt-realtime-mini</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2 items-center mb-4">
                <label className="text-sm text-gray-600">Voice</label>
                <select
                  className="border rounded px-2 py-1 text-sm"
                  value={runtimeVoice}
                  onChange={(e) => setRuntimeVoice(e.target.value)}
                >
                  <option value="marin">marin</option>
                  <option value="coral">coral</option>
                  <option value="verse">verse</option>
                </select>
              </div>
              <div className="space-y-2">
                {activeVariablesForRun.map((v) => (
                  <div key={v.variableName} className="grid grid-cols-2 gap-2 items-center">
                    <label className="text-sm text-gray-600">{v.variableName}</label>
                    <select
                      className="border rounded px-2 py-1 text-sm"
                      value={runValues[v.variableName] || ''}
                      onChange={(e) => setRunValues(prev => ({ ...prev, [v.variableName]: e.target.value }))}
                    >
                      <option value="">{'(blank)'}</option>
                      {(v.allowedValues || []).map(val => (<option key={val} value={val}>{val}</option>))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-700 mb-1">Assembled Prompt</div>
              <pre className="whitespace-pre-wrap text-sm border rounded p-3 bg-gray-50 max-h-[400px] overflow-auto">
                {previewText || 'Nothing to preview yet.'}
              </pre>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'variables' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-6">
          <div className="lg:col-span-6">
            {loading && <div className="text-sm text-gray-500 mb-2">Loading...</div>}
            <div className="space-y-2">
              {variables.map(v => {
                const isSelected = selectedVariable?.variableName === v.variableName;
                return (
                  <div
                    key={v.variableName}
                    className={`border rounded p-3 flex items-center justify-between bg-white cursor-pointer ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
                    onClick={() => setSelectedVariable(v)}
                  >
                    <div>
                      <div className="font-medium text-gray-900">{v.variableName}</div>
                      <div className="text-xs text-gray-500">{v.description}</div>
                      <div className="text-xs text-gray-400">
                        {`Created by ${v.createdBy || 'system'}`}{v.createdAt ? ` • ${new Date(v.createdAt).toLocaleString()}` : ''}
                      </div>
                      <div className="text-xs text-gray-500">Mode: {v.substitutionMode} • Type: {v.type}</div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        className="px-2 py-1 text-sm border rounded hover:bg-gray-50"
                        onClick={(e) => { e.stopPropagation(); setVarEditor({ ...v, isNew: false }); }}
                      >
                        Edit
                      </button>
                      <button
                        className="px-2 py-1 text-sm border rounded hover:bg-gray-50"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm(`Archive variable "${v.variableName}"?`)) return;
                          try {
                            setLoading(true);
                            await variableService.archive(v.variableName);
                            await load();
                            if (selectedVariable?.variableName === v.variableName) {
                              setSelectedVariable(null);
                            }
                          } catch (e: any) {
                            setError(e?.message || 'Failed to archive variable');
                          } finally {
                            setLoading(false);
                          }
                        }}
                      >
                        Archive
                      </button>
                    </div>
                  </div>
                );
              })}
              {variables.length === 0 && !loading && (
                <div className="text-sm text-gray-500">No variables yet.</div>
              )}
            </div>
          </div>
          <div className="lg:col-span-6">
            {varEditor ? (
              <div className="border rounded p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-800">{varEditor.isNew ? 'Create Variable' : 'Edit Variable'}</h3>
                  <button className="px-2 py-1 border rounded text-sm hover:bg-gray-50" onClick={() => setVarEditor(null)}>Close</button>
                </div>
                {!varEditor.isNew && (
                  <div className="mb-3 text-xs text-gray-500">
                    <span>{`Created by ${varEditor.createdBy || 'system'}`}</span>
                    {varEditor.createdAt ? <span>{` • Created ${new Date(varEditor.createdAt).toLocaleString()}`}</span> : null}
                    {varEditor.updatedAt ? <span>{` • Updated ${new Date(varEditor.updatedAt).toLocaleString()}`}</span> : null}
                  </div>
                )}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Variable Name</label>
                    <input
                      className="w-full border rounded px-3 py-2"
                      value={varEditor.variableName || ''}
                      onChange={(e) => setVarEditor(s => ({ ...(s as any), variableName: e.target.value }))}
                      placeholder="e.g., user_name"
                    />
                    <div className="text-xs text-gray-500 mt-1">{`Used as {{variableName}} placeholder, <= 50 chars.`}</div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Description</label>
                    <input
                      className="w-full border rounded px-3 py-2"
                      value={varEditor.description || ''}
                      onChange={(e) => setVarEditor(s => ({ ...(s as any), description: e.target.value }))}
                      placeholder="Short description (<= 250 chars)"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Type</label>
                      <select
                        className="w-full border rounded px-3 py-2"
                        value={(varEditor.type as any) || 'string'}
                        onChange={(e) => setVarEditor(s => ({ ...(s as any), type: e.target.value as any }))}
                      >
                        <option value="string">String</option>
                        <option value="number">Number</option>
                        <option value="string_array">Array of strings</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Substitution Mode</label>
                      <select
                        className="w-full border rounded px-3 py-2"
                        value={(varEditor.substitutionMode as any) || 'value'}
                        onChange={(e) => setVarEditor(s => ({ ...(s as any), substitutionMode: e.target.value as any }))}
                      >
                        <option value="value">Value</option>
                        <option value="component">Component</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Allowed Values (one per line)</label>
                    <textarea
                      className="w-full border rounded px-3 py-2 min-h-[120px]"
                      value={(varEditor.allowedValues || []).join('\n')}
                      onChange={(e) => setVarEditor(s => ({ ...(s as any), allowedValues: e.target.value.split('\n') }))}
                      placeholder="Enter one value per line"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Default</label>
                    <select
                      className="w-full border rounded px-3 py-2"
                      value={varEditor.defaultValue || ''}
                      onChange={(e) => setVarEditor(s => ({ ...(s as any), defaultValue: e.target.value || undefined }))}
                    >
                      <option value="">(none)</option>
                      {(varEditor.allowedValues || []).map(v => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                    <div className="text-xs text-gray-500 mt-1">Must be one of the allowed values.</div>
                  </div>
                  {varEditor.substitutionMode === 'component' && (
                    <div>
                      <label className="block text-sm text-gray-700 mb-2">Component Text per Allowed Value</label>
                      <div className="space-y-3">
                        {(varEditor.allowedValues || []).map(val => {
                          const map = (varEditor.componentMap || {}) as Record<string, { text: string }>;
                          const entry = map[val] || { text: '' };
                          return (
                            <div key={val}>
                              <div className="text-sm font-medium text-gray-700 mb-1">{val}</div>
                              <textarea
                                className="w-full border rounded px-3 py-2 min-h-[100px]"
                                value={entry.text}
                                onChange={(e) => {
                                  const newMap = { ...(varEditor.componentMap || {}) } as Record<string, { text: string }>;
                                  newMap[val] = { text: e.target.value };
                                  setVarEditor(s => ({ ...(s as any), componentMap: newMap }));
                                }}
                                placeholder={`Prompt text to inject when ${val} is selected`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <button
                      className="px-3 py-1 border rounded text-sm hover:bg-gray-50"
                      onClick={() => setVarEditor(null)}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                      onClick={async () => {
                        try {
                          setLoading(true);
                          setError(null);
                          const payload: any = {
                            variableName: varEditor.variableName,
                            description: varEditor.description,
                            type: varEditor.type || 'string',
                            substitutionMode: varEditor.substitutionMode || 'value',
                            allowedValues: (varEditor.allowedValues || []).map(v => (v ?? '').trim()).filter(Boolean),
                            defaultValue: varEditor.defaultValue || undefined,
                            componentMap: varEditor.substitutionMode === 'component' ? (varEditor.componentMap || {}) : undefined,
                            status: (varEditor.status as any) || 'active',
                            createdBy: currentUserEmail || 'system'
                          };
                          if (varEditor.isNew) {
                            await variableService.create(payload);
                          } else if (varEditor.variableName) {
                            await variableService.update(varEditor.variableName, payload);
                          }
                          setVarEditor(null);
                          await load();
                        } catch (e: any) {
                          setError(e?.message || 'Failed to save variable');
                        } finally {
                          setLoading(false);
                        }
                      }}
                    >
                      Save Variable
                    </button>
                  </div>
                </div>
              </div>
            ) : selectedVariable ? (
              <div className="border rounded p-4 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-800">{selectedVariable.variableName}</h3>
                  <button
                    className="px-3 py-1 border rounded text-sm hover:bg-gray-50"
                    onClick={() => setVarEditor({ ...selectedVariable, isNew: false })}
                  >
                    Edit
                  </button>
                </div>
                <div className="text-sm text-gray-700 space-y-2">
                  <div><span className="font-medium">Description:</span> {selectedVariable.description || '—'}</div>
                  <div><span className="font-medium">Type:</span> {selectedVariable.type}</div>
                  <div><span className="font-medium">Substitution Mode:</span> {selectedVariable.substitutionMode}</div>
                  <div><span className="font-medium">Default:</span> {selectedVariable.defaultValue ?? '—'}</div>
                  <div>
                    <span className="font-medium">Allowed Values:</span>
                    <ul className="list-disc list-inside">
                      {(selectedVariable.allowedValues || []).map((av) => (
                        <li key={av}>
                          {av}
                          {selectedVariable.substitutionMode === 'component' && (selectedVariable.componentMap as any)?.[av]?.text ? (
                            <div className="text-xs text-gray-600 mt-1 border rounded p-2 bg-gray-50">
                              {(selectedVariable.componentMap as any)[av].text}
                            </div>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="text-xs text-gray-500">
                    {`Created by ${selectedVariable.createdBy || 'system'}`} {selectedVariable.createdAt ? ` • ${new Date(selectedVariable.createdAt).toLocaleString()}` : ''}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">Select a variable to view details or click “New Variable”.</div>
            )}
          </div>
        </div>
      )}
      
      {activeTab === 'profiles' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-6">
          <div className="lg:col-span-6">
            {loading && <div className="text-sm text-gray-500 mb-2">Loading...</div>}
            <div className="space-y-2">
              {profiles.map(p => {
                const isSelected = selectedProfile?.profileName === p.profileName;
                return (
                  <div
                    key={p.profileName}
                    className={`border rounded p-3 flex items-center justify-between bg-white cursor-pointer ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
                    onClick={() => selectProfile(p)}
                  >
                    <div>
                      <div className="font-medium text-gray-900">{p.profileName}</div>
                      <div className="text-xs text-gray-500">{p.description || '—'}</div>
                      <div className="text-xs text-gray-400">
                        {`Created by ${p.createdBy || 'system'}`}{p.createdAt ? ` • ${new Date(p.createdAt).toLocaleString()}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${p.status === 'active' ? 'bg-green-100 text-green-800' : p.status === 'draft' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'}`}>
                        {p.status}
                      </span>
                      <button
                        className="px-2 py-1 text-sm border rounded hover:bg-gray-50"
                        onClick={(e) => { e.stopPropagation(); beginEditProfile(p); }}
                      >
                        Edit
                      </button>
                      <button
                        className="px-2 py-1 text-sm border rounded hover:bg-gray-50"
                        onClick={(e) => { e.stopPropagation(); archiveProfile(p); }}
                      >
                        Archive
                      </button>
                    </div>
                  </div>
                );
              })}
              {profiles.length === 0 && !loading && (
                <div className="text-sm text-gray-500">No profiles yet. Click “New Profile”.</div>
              )}
            </div>
          </div>
          <div className="lg:col-span-6">
            {profileEditor ? (
              <div className="border rounded p-4 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-800">{profileEditor.isNew ? 'Create Profile' : 'Edit Profile'}</h3>
                  <button className="px-2 py-1 border rounded text-sm hover:bg-gray-50" onClick={cancelProfileEdit}>Close</button>
                </div>
                {!profileEditor.isNew && (
                  <div className="mb-3 text-xs text-gray-500">
                    <span>{`Created by ${profileEditor.createdBy || 'system'}`}</span>
                    {profileEditor.createdAt ? <span>{` • Created ${new Date(profileEditor.createdAt).toLocaleString()}`}</span> : null}
                    {profileEditor.updatedAt ? <span>{` • Updated ${new Date(profileEditor.updatedAt).toLocaleString()}`}</span> : null}
                  </div>
                )}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Profile Name</label>
                    <input
                      className="w-full border rounded px-3 py-2"
                      value={profileEditor.profileName || ''}
                      onChange={(e) => setProfileEditor(s => ({ ...(s as any), profileName: e.target.value }))}
                      placeholder="e.g., intermediate_spanish_speaker"
                      disabled={!profileEditor.isNew}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Description</label>
                    <input
                      className="w-full border rounded px-3 py-2"
                      value={profileEditor.description || ''}
                      onChange={(e) => setProfileEditor(s => ({ ...(s as any), description: e.target.value }))}
                      placeholder="Describe the profile intent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Status</label>
                    <select
                      className="w-full border rounded px-3 py-2"
                      value={(profileEditor.status as any) || 'active'}
                      onChange={(e) => setProfileEditor(s => ({ ...(s as any), status: e.target.value as any }))}
                    >
                      <option value="active">Active</option>
                      <option value="draft">Draft</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-2">Variable Values</label>
                    <div className="space-y-2">
                      {variables.map(v => {
                        const current = (profileEditor.values || {})[v.variableName] || '';
                        return (
                          <div key={v.variableName} className="grid grid-cols-2 gap-2 items-center">
                            <label className="text-sm text-gray-600">{v.variableName}</label>
                            <select
                              className="border rounded px-2 py-1 text-sm"
                              value={current}
                              onChange={(e) => {
                                const next = { ...(profileEditor.values || {}) };
                                const val = e.target.value;
                                if (val === '') {
                                  delete next[v.variableName];
                                } else {
                                  next[v.variableName] = val;
                                }
                                setProfileEditor(s => ({ ...(s as any), values: next }));
                              }}
                            >
                              <option value="">{'(blank)'}</option>
                              {(v.allowedValues || []).map(val => (<option key={val} value={val}>{val}</option>))}
                            </select>
                          </div>
                        );
                      })}
                      {variables.length === 0 && (
                        <div className="text-xs text-gray-500">No active variables found.</div>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      className="px-3 py-1 border rounded text-sm hover:bg-gray-50"
                      onClick={cancelProfileEdit}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                      onClick={saveProfile}
                    >
                      Save Profile
                    </button>
                  </div>
                </div>
              </div>
            ) : selectedProfile ? (
              <div className="border rounded p-4 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-800">{selectedProfile.profileName}</h3>
                  <button
                    className="px-3 py-1 border rounded text-sm hover:bg-gray-50"
                    onClick={() => beginEditProfile(selectedProfile)}
                  >
                    Edit
                  </button>
                </div>
                <div className="text-sm text-gray-700 space-y-2">
                  <div><span className="font-medium">Description:</span> {selectedProfile.description || '—'}</div>
                  <div><span className="font-medium">Status:</span> {selectedProfile.status}</div>
                  <div className="text-xs text-gray-500">
                    {`Created by ${selectedProfile.createdBy || 'system'}`} {selectedProfile.createdAt ? ` • ${new Date(selectedProfile.createdAt).toLocaleString()}` : ''}
                  </div>
                  <div className="mt-2">
                    <div className="font-medium text-sm text-gray-700 mb-1">Values:</div>
                    {selectedProfile.values && Object.keys(selectedProfile.values).length > 0 ? (
                      <ul className="list-disc list-inside text-sm">
                        {Object.entries(selectedProfile.values || {}).map(([k, v]) => (
                          <li key={k}><span className="text-gray-600">{k}:</span> {v}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-xs text-gray-500">No values set.</div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">Select a profile to view details or click “New Profile”.</div>
            )}
          </div>
        </div>
      )}
      
      {activeTab === 'tools' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-6">
          <div className="lg:col-span-6">
            <div className="space-y-2">
              {TOOL_DEFS.map((t, idx) => {
                const isSelected = selectedToolIdx === idx;
                return (
                  <div
                    key={t.name}
                    className={`border rounded p-3 bg-white cursor-pointer ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
                    onClick={() => setSelectedToolIdx(idx)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">{t.name}</div>
                        <div className="text-xs text-gray-500">{t.description}</div>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-600">
                      <span className="font-medium">Trigger:</span> {t.trigger}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="border rounded p-4 bg-white">
              {TOOL_DEFS[selectedToolIdx] ? (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-800">{TOOL_DEFS[selectedToolIdx].name}</h3>
                    <button
                      className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                      onClick={() => {
                        try {
                          const payload = {
                            name: TOOL_DEFS[selectedToolIdx].name,
                            description: TOOL_DEFS[selectedToolIdx].description,
                            trigger: TOOL_DEFS[selectedToolIdx].trigger,
                            parametersSchema: TOOL_DEFS[selectedToolIdx].parametersSchema,
                            returns: TOOL_DEFS[selectedToolIdx].returns
                          };
                          navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
                        } catch {}
                      }}
                    >
                      Copy Details
                    </button>
                  </div>
                  <div className="text-sm text-gray-700 mb-2">
                    {TOOL_DEFS[selectedToolIdx].description}
                  </div>
                  <div className="text-sm text-gray-700 mb-2">
                    <span className="font-medium">Trigger:</span> {TOOL_DEFS[selectedToolIdx].trigger}
                  </div>
                  <div className="mb-3">
                    <div className="text-sm text-gray-700 mb-1">Parameters Schema</div>
                    <pre className="whitespace-pre-wrap text-xs border rounded p-3 bg-gray-50 max-h-[300px] overflow-auto">
                      {JSON.stringify(TOOL_DEFS[selectedToolIdx].parametersSchema || {}, null, 2)}
                    </pre>
                  </div>
                  <div>
                    <div className="text-sm text-gray-700 mb-1">Typical Return</div>
                    <pre className="whitespace-pre-wrap text-xs border rounded p-3 bg-gray-50 max-h-[200px] overflow-auto">
                      {TOOL_DEFS[selectedToolIdx].returns || '—'}
                    </pre>
                  </div>
                </>
              ) : (
                <div className="text-sm text-gray-500">Select a tool to view details.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptStudio;


